import React, { useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import moment, { Moment } from 'moment'
import { colors, Link } from '@resiliatech/birdsong'
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material'
import { IMPACT_DASHBOARD_GOALS } from 'Apollo/Queries/ImpactDashboardGoals'
import UPSERT_CHECKLIST_ITEM from 'Apollo/Mutations/UpsertChecklistItem'
import { useAppState } from 'Context/AppContext/AppContext'
import { Goal } from 'Generated/graphql-types'
import useNonprofitOrgTeam from 'Hooks/useNonprofitOrgTeam/useNonprofitOrgTeam'
import { ContentPasteOff } from '@mui/icons-material'

import GET_ACADEMY_RESOURCES from 'ApolloAcademy/Queries/GetAcademyResources'
import {
  ACADEMY_CONTEXT,
  buildResourcePath,
} from 'Components/Pages/Academy/_common/utils'
import {
  ResourceSortOptions,
  ResourceType,
} from 'Generated/academy-graphql-types'
import { Box, Tooltip, Typography } from '@material-ui/core'
import { useSnackbar } from 'Context/SnackbarContext/SnackbarContext'
import {
  dispatchAcademyResourceClicked,
  dispatchGoalTaskAssignedEvent,
  dispatchGoalTaskAssignedNotificationSentEvent,
  dispatchGoalTaskCompletedEvent,
  dispatchGoalTaskDueDateDeleteClickedEvent,
  dispatchGoalTaskDueDateSubmittedEvent,
  dispatchTaskClicked,
} from '../analytics.utils'
import { TasksByGroupTabs } from './TasksByGroupTabs'
import {
  ActionsContainer,
  ContentContainer,
  ResourceInfo,
  TasksContainer,
  TaskTableContainer,
  TaskTitleContainer,
} from './TaskDigest.styled'
import { WithWidgetLoading } from '../WithWidgetLoading'
import {
  CheckListItemWithGoalInfo,
  DashboardFilters,
  type Tab as TabType,
  TabFilterKeyEnum,
} from '../types'
import { Header } from '../ImpactReportDashboard.styled'
import {
  filterTasks,
  getSortedTasks,
  getTasksFromGoals,
  getTasksToDisplay,
  ISO_8601_DATE_FORMAT,
  TASK_DATE_FORMAT,
  truncateText,
} from '../utils'
import { TaskCheckbox } from './TaskCheckbox'
import { TaskDueDate } from './TaskDueDate'
import { TaskOwner } from './TaskOwner'
import { EmptyState } from '../EmptyState/EmptyState'
import { GOALS_PATH } from '../../GoalsDashboard/types'
import { ProgressUpdates } from '../../GoalPage/ProgressUpdate/ProgressUpdates/ProgressUpdates'

type TaskDigestProps = {
  filters: DashboardFilters
  loading: boolean
  goals: Goal[]
}

export const TaskDigest = ({ filters, loading, goals }: TaskDigestProps) => {
  const { teamMap = {} } = useNonprofitOrgTeam()
  const { popToast } = useSnackbar()
  const {
    nonprofitOrgConfig: { orgId, platformPlan, funderOrgId },
    userConfig: { id: userId, email: userEmail },
  } = useAppState()
  const [showProgressUpdate, setShowProgressUpdate] = useState(false)
  const [activeTab, setActiveTab] = useState(TabFilterKeyEnum.unassigned)
  const [activeTask, setActiveTask] =
    useState<CheckListItemWithGoalInfo | null>(null)
  const [sortKey, setSortKey] = useState<string | null>(null)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [upsertChecklistItem] = useMutation(UPSERT_CHECKLIST_ITEM)
  const allTasks = getTasksFromGoals(goals)
  const REDIRECT_TIMEOUT_IN_MS = 5000

  const getResourceIdsForItems = (tasks: CheckListItemWithGoalInfo[]) =>
    tasks
      .map((task) => task.resourceId)
      .filter((resourceId) => resourceId !== null)

  const { data: resourcesData } = useQuery(GET_ACADEMY_RESOURCES, {
    ...ACADEMY_CONTEXT,
    variables: {
      ids: getResourceIdsForItems(allTasks),
      resourceType: [
        ResourceType.Article,
        ResourceType.Course,
        ResourceType.Template,
        ResourceType.LearningTrack,
      ],
      query: '',
      sort: ResourceSortOptions.Random,
      anonymousId: global.analytics?.user?.()?.anonymousId?.(),
    },
    skip: !getResourceIdsForItems(allTasks).length,
  })

  const handleSort = (columnId: string) => {
    if (sortKey === columnId) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(columnId)
      setSortOrder('asc')
    }
  }

  const updateTask = async (task: CheckListItemWithGoalInfo) => {
    const itemInfo = {
      dueDate: task?.dueDate || undefined,
      goalId: task.goal.id,
      id: task?.id,
      isCompleted: task?.isCompleted,
      linkText: task?.linkText,
      linkUrl: task?.linkUrl,
      ownerId: task?.owner?.id || null,
      resourceId: task?.resourceId ?? null,
      title: task?.title || '',
    }

    await upsertChecklistItem({
      variables: {
        itemInfo,
        nonprofitOrgId: orgId,
      },
      refetchQueries: [IMPACT_DASHBOARD_GOALS],
    })
  }

  const handleToggleTaskCompleteStatus = async (
    task: CheckListItemWithGoalInfo
  ) => {
    const currentOwner = task.owner?.id
    const assigneeUserEmail = currentOwner ? teamMap[currentOwner].email : null
    const isCompleted = !task.isCompleted

    await updateTask({ ...task, isCompleted })

    if (isCompleted) {
      dispatchGoalTaskCompletedEvent({
        assigneeUserEmail,
        task,
        userId,
      })
      popToast({
        content: `You've marked one task as completed`,
        timeout: REDIRECT_TIMEOUT_IN_MS / 1000,
      })
      setActiveTask(task)
      setShowProgressUpdate(true)
    }
  }

  const handleChangeTaskDueDate = (
    date: Moment | null,
    task: CheckListItemWithGoalInfo
  ) => {
    const newDueDate = date ? date.format(TASK_DATE_FORMAT) : null
    if (newDueDate) {
      dispatchGoalTaskDueDateSubmittedEvent({
        newDueDate,
        task,
        userId,
      })
    } else {
      dispatchGoalTaskDueDateDeleteClickedEvent({
        newDueDate,
        task,
        userId,
      })
    }
    const updatedTask = {
      ...task,
      dueDate: newDueDate,
    }

    updateTask(updatedTask)
  }

  const handleChangeTaskOwner = async (
    newOwnerId: number,
    task: CheckListItemWithGoalInfo
  ) => {
    if (newOwnerId === 0) {
      updateTask({ ...task, owner: null })
    }

    if (newOwnerId) {
      const selectedOwner = { ...teamMap[newOwnerId], id: newOwnerId }
      await updateTask({
        ...task,
        owner: selectedOwner,
      })

      dispatchGoalTaskAssignedEvent({
        assigneeUserEmail: selectedOwner.email,
        task,
        newOwnerId,
        userId,
      })

      if (userId !== newOwnerId) {
        dispatchGoalTaskAssignedNotificationSentEvent({
          task,
          userId,
        })
      }
    }
  }

  const handleCloseProgressModal = () => {
    setShowProgressUpdate(false)
    setActiveTask(null)
  }

  const totalTasks: CheckListItemWithGoalInfo[] = []
  const incompleteTasks: CheckListItemWithGoalInfo[] = []
  const completedTasks: CheckListItemWithGoalInfo[] = []
  const overdueTasks: CheckListItemWithGoalInfo[] = []
  const unassignedTasks: CheckListItemWithGoalInfo[] = []

  // Filter tasks based on user preferences
  const filteredTasks = filterTasks(allTasks, filters)

  filteredTasks.forEach((task) => {
    const dueDate = moment(task.dueDate, 'YYYY-MM-DD')
    totalTasks.push(task)
    if (task.isCompleted) completedTasks.push(task)
    else incompleteTasks.push(task)
    if (!task.owner) unassignedTasks.push(task)
    if (
      !task.isCompleted &&
      dueDate.isValid() &&
      dueDate.isBefore(moment().subtract(1, 'days'))
    )
      overdueTasks.push(task)
  })

  const numTotalTasks = totalTasks.length
  const numIncompleteTasks = incompleteTasks.length
  const numCompletedTasks = completedTasks.length
  const numOverdueTasks = overdueTasks.length
  const numUnassignedTasks = unassignedTasks.length

  const tasksToDisplay = getTasksToDisplay({
    activeTab,
    completedTasks,
    incompleteTasks,
    overdueTasks,
    unassignedTasks,
    totalTasks,
  })

  const sortedTasks = getSortedTasks({
    sortKey,
    sortOrder,
    tasksToDisplay,
  })

  const columns = [
    { sortKey: 'title', label: 'Task' },
    { sortKey: 'goalTitle', label: 'Goal Title' },
    { sortKey: 'dueDate', label: 'Due Date' },
    { sortKey: '', label: '' }, // Empty column for action buttons
  ]

  const tabs: TabType[] = [
    {
      count: numUnassignedTasks,
      filterKey: TabFilterKeyEnum.unassigned,
      label: 'Unassigned',
    },
    {
      count: numOverdueTasks,
      filterKey: TabFilterKeyEnum.pastDue,
      label: 'Past Due',
    },
    {
      count: numIncompleteTasks,
      filterKey: TabFilterKeyEnum.todo,
      label: 'To Do',
    },
    {
      count: numCompletedTasks,
      filterKey: TabFilterKeyEnum.completed,
      label: 'Completed',
    },
    { count: numTotalTasks, filterKey: TabFilterKeyEnum.all, label: 'All' },
  ]

  const isEmptyState = !loading && totalTasks.length === 0

  return (
    <WithWidgetLoading
      loading={loading}
      width='100%'
      height={isEmptyState ? 'auto' : '520px'}
    >
      <Header>Tasks</Header>
      <TasksContainer>
        {isEmptyState ? (
          <EmptyState
            icon={ContentPasteOff}
            title='No Tasks'
            description='All Tasks will appear here once you and your team create them.'
          />
        ) : (
          <ContentContainer>
            <TasksByGroupTabs
              activeTab={activeTab}
              onChangeTab={(tab) => setActiveTab(tab.filterKey)}
              tabs={tabs}
            />
            <Paper variant='outlined'>
              <TaskTableContainer>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      {columns.map((c) => (
                        <TableCell
                          key={c.sortKey}
                          sx={{
                            backgroundColor:
                              colors.background.secondaryBackground,
                          }}
                        >
                          {c.sortKey && (
                            <TableSortLabel
                              active={sortKey === c.sortKey}
                              direction={
                                sortKey === c.sortKey ? sortOrder : 'asc'
                              }
                              onClick={() => handleSort(c.sortKey)}
                              sx={{ minWidth: '100px' }}
                            >
                              {c.label}
                            </TableSortLabel>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedTasks.map((task) => {
                      const resource = resourcesData?.resources.nodes.find(
                        (r: { id: string | undefined }) =>
                          r.id === task.resourceId
                      )
                      const resourcePath = resource
                        ? buildResourcePath(
                            resource.type === 'LearningTrack'
                              ? { id: resource.id, type: 'learning_track' }
                              : resource
                          )
                        : null

                      return (
                        <TableRow
                          key={task.id}
                          sx={{
                            // eslint-disable-next-line @typescript-eslint/naming-convention
                            '&:hover': {
                              backgroundColor: colors.illustration.lightBlue10,
                            },
                          }}
                        >
                          <TableCell>
                            <TaskTitleContainer>
                              <Box>
                                <TaskCheckbox
                                  onToggleTaskComplete={
                                    handleToggleTaskCompleteStatus
                                  }
                                  task={task}
                                />
                                <Link
                                  href={`${GOALS_PATH}/${task.goal.id}?section=tasks&taskId=${task.id}`}
                                  target='_blank'
                                  rel='noopener'
                                  style={{
                                    color: task.isCompleted
                                      ? `${colors.text.disabled}`
                                      : 'inherit',
                                  }}
                                  onClick={() => {
                                    dispatchTaskClicked({
                                      goalId: task.goal.id,
                                      goalTitle: task.goal.title,
                                      funderOrgId: funderOrgId || null,
                                      isCompleted: task.isCompleted,
                                      taskId: task.id,
                                      taskTitle: task.title,
                                      orgId,
                                      platformPlan: platformPlan || '',
                                      userEmail,
                                      userId,
                                    })
                                  }}
                                >
                                  {task.title}
                                </Link>
                              </Box>
                              {resource && resourcePath && (
                                <ResourceInfo>
                                  <Link
                                    href={resourcePath}
                                    target='_blank'
                                    rel='noopener'
                                    onClick={() => {
                                      dispatchAcademyResourceClicked({
                                        academyReourceTitle: resource.title,
                                        academyResourceType: resource.type,
                                        academyResourceCategory:
                                          resource.category,
                                        academyResourceId: resource.id,
                                        funderOrgId: funderOrgId || null,
                                        orgId,
                                        platformPlan: platformPlan || '',
                                        userEmail,
                                        userId,
                                      })
                                    }}
                                  >
                                    {resource.title}
                                  </Link>
                                </ResourceInfo>
                              )}
                            </TaskTitleContainer>
                          </TableCell>
                          <Tooltip
                            title={
                              <Box sx={{ p: 2, maxWidth: 300 }}>
                                <Typography>Goal: {task.goal.title}</Typography>
                              </Box>
                            }
                            arrow
                            placement='left'
                          >
                            <TableCell>
                              {truncateText(task.goal.title, 20)}
                            </TableCell>
                          </Tooltip>
                          <TableCell>
                            {task.dueDate
                              ? moment
                                  .utc(task.dueDate, ISO_8601_DATE_FORMAT)
                                  .format(TASK_DATE_FORMAT)
                              : ''}
                          </TableCell>
                          <TableCell>
                            <ActionsContainer>
                              <TaskDueDate
                                onChangeTaskDueDate={(date: Moment | null) =>
                                  handleChangeTaskDueDate(date, task)
                                }
                                task={task}
                              />
                              <TaskOwner
                                onChangeTaskOwner={(newOwnerId: number) =>
                                  handleChangeTaskOwner(newOwnerId, task)
                                }
                                owner={task.owner ?? null}
                              />
                            </ActionsContainer>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TaskTableContainer>
            </Paper>
          </ContentContainer>
        )}
        {activeTask && (
          <ProgressUpdates
            open={showProgressUpdate}
            onClose={handleCloseProgressModal}
            goalId={activeTask.goal.id}
            goal={activeTask.goal}
            taskId={activeTask.id}
          />
        )}
      </TasksContainer>
    </WithWidgetLoading>
  )
}
